import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getImage } from "redux/slices/getImageSlice";
import LoadingOverlay from "react-loading-overlay";
import * as fileSaver from "file-saver";
import moment from "moment";

import {
  stringFormattingToCurrency,
  stringFormatingToPascal,
  getPolicyType,
  getName,
  base64ToArrayBuffer,
  odsClaimStatusConversion,
  getPolicyNumberTitle,
  getBadgeText,
  convertToTitleCase,
  getServiceRequestDate,
} from "js/formatting";

import { getFormDocument } from "service/forms/getForms";
import { getFinancials } from "service/policy/getFinancials";
import {
  getTypeOfPolicy,
  isAllowedToMakeCCPayment,
  isFlagEnabled,
  isOldAlicUlPolicy,
  isPolicyInGrace,
} from "js/utilities";
import { downloadingForm } from "service/forms/downloadingForm";
import { useTranslation, Trans } from "react-i18next";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";
import Badge from "components/General/Badge";
import StatusDot from "components/General/StatusDot";
import Pill from "components/General/Pill";
import AssistantInfoDialog from "components/AgentPortal/AgentProfile/Dialogs/AssistantInfoDialog";
import AssistantTerminateDialog from "components/AgentPortal/AgentProfile/Dialogs/AssistantTerminateDialog";
import AgentAssistantReminderModel from "components/AgentPortal/ModalDialogs/AgentAssistantReminderModel";
import PDFDialog from "components/ModalDialogs/PDFDialog";
import useTruckDownloadForm from "logging/hooks/useTruckDownloadForm";

export default function SimpleDataRow({
  type,
  data,
  setIsOpenSendReminder,
  setPolicyId,
  setAmount,
  getuserPermissions,
  setPageLoad,
  setOldPolicyId,
  formSearchType,
  hideFormSubTitle = false,
  isCSRUser = false,
  onSendEmailClick,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  LoadingOverlay.propTypes = undefined;

  const truckDownloadForm = useTruckDownloadForm();
  const { user } = useSelector((s) => s.user);
  const [loading, setLoading] = useState(false);
  const [pdfSource, setPdfSource] = useState("");
  const [desc, setDesc] = useState("");
  const [lapsedOn, setLapsedOn] = useState();
  const [pdfDialogState, setPdfDialogState] = useState(false);
  const [openAgentAssistantDialog, setopenAgentAssistantDialog] =
    useState(false);
  const [
    openTerminateAgentAssistantDialog,
    setopenTerminateAgentAssistantDialog,
  ] = useState(false);
  const [
    openReminderAgentAssistantDialog,
    setopenReminderAgentAssistantDialog,
  ] = useState(false);
  const [openAgentReactivateDialog, setopenAgentReactivateDialog] =
    useState(false);
  let dynamicMarkUp = "";
  useEffect(() => {
    if (type === "bookOfBusiness") setLapsedOn(data?.lapseDate);
  }, [data]);

  async function getPdfFormDocument(data) {
    setLoading(true);
    if (isFlagEnabled("ENABLE_FORMS_WIZARD")) {
      await downloadingForm(data?.id)
        .then((response) => {
          setLoading(false);
          let byteArray = base64ToArrayBuffer(response?.result);
          let blob = new Blob([byteArray], {
            type: t("AgentSimpleDataRow-applicationpdf"),
          });
          fileSaver.saveAs(blob, `${data?.formNumber}.pdf`);
        })
        .catch((error) => {
          setPdfDialogState(true);
          setLoading(false);
          setPdfSource("");
        });
    } else {
      await getFormDocument(data?.templateId, data?.company)
        .then((response) => {
          setLoading(false);
          let byteArray = base64ToArrayBuffer(response?.result);
          let blob = new Blob([byteArray], {
            type: t("AgentSimpleDataRow-applicationpdf"),
          });
          fileSaver.saveAs(blob, `${data?.formNumber}.pdf`);
        })
        .catch((error) => {
          setPdfDialogState(true);
          setLoading(false);
          setPdfSource("");
        });
    }
    truckDownloadForm({
      ...user,
      formName: data?.formTitle,
      formTitle: data?.formTitle,
      productType: data?.productType,
      company: data?.company,
      issueState: data?.issueState,
      formID: data?.templateId,
      searchType: formSearchType ?? "",
    });
  }

  async function getLapseOn(data) {
    setLoading(true);
    await getFinancials({ data, contractID: data?.policyId })
      .then((response) => {
        const lapseDate = response.cashValues?.find(
          (cashValue) => moment(cashValue.projectedlapseDate) > moment()
        )?.projectedlapseDate;

        setLapsedOn(lapseDate);
        setLoading(false);
      })
      .catch((error) => {
        console.error("getFinancials failing", error);
        setLoading(false);
      });
  }

  switch (type) {
    case "reportDetail":
      dynamicMarkUp = (
        <div className="flex flex-col-reverse md:flex-row">
          <div className="flex-grow">
            <div className="grid gap-2 grid-cols-1 sm:gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {getPolicyNumberTitle(data)} {data.oldPolicy} :{" "}
                  {getPolicyType(data)}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data.ownerName}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-coverageText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data.coverage}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-issueDateText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data.issueDate}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-maturityDateText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data?.coverageEndDate &&
                    moment(data?.coverageEndDate).format("MM/DD/YYYY")}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-cashValueText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data.value}
                </div>
              </div>
            </div>
          </div>
          <div className="md:w-40 md:text-right">
            {data.status && (
              <div className="mb-4">
                {data?.status && data?.status !== "" && (
                  <Badge
                    status={data.status}
                    statusText={getBadgeText(data)}
                    srPrefix={t("SimpleDataRow-badge-srPrefix")}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      );
      break;
    case "commission":
      dynamicMarkUp = (
        <>
          <LoadingOverlay
            active={loading}
            spinner={
              <EverlakeLoadingSpinner
                spinnerSize="xs"
                loadingTextMargin="2"
                loadingText={t("SimpleDataRow-commission-spinnerLoadingText")}
              />
            }
            styles={EverlakeLoadingOverlayStyle}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:gap-6">
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-commission-monthText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {moment(data?.document?.dateOfDocument).format("MMMM")}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary"> </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {" "}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-commission-statementDateText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  <div>
                    {moment(data?.document?.dateOfDocument).format(
                      "MM/DD/YYYY"
                    )}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary"> </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {" "}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-commission-youCanText")}
                </div>
                <div>
                  <button
                    className="ui-btn-hyperlink font-semibold"
                    onClick={() => {
                      if (data?.document?.name) {
                        setLoading(true);
                        dispatch(
                          getImage({
                            imageFileName: data?.document?.name ?? "",
                            policyId: data?.policy?.contractID,
                          })
                        )
                          .then((response) => {
                            setLoading(false);
                            setDesc(data?.document?.description);
                            if (response?.meta?.requestStatus === "fulfilled") {
                              let byteArray = base64ToArrayBuffer(
                                response.payload.document.contentStream.type
                              );
                              let blob = new Blob([byteArray], {
                                type: t("AgentSimpleDataRow-applicationpdf"),
                              });
                              fileSaver.saveAs(
                                blob,
                                `${data?.document?.description}.pdf`
                              );
                            }
                          })
                          .catch((error) => {
                            console.error("Error", error.message);
                            setPdfDialogState(true);
                            setLoading(false);
                            setDesc(data?.document?.document?.description);
                            setPdfSource("");
                          });
                      }
                    }}
                  >
                    <Trans
                      i18nKey="SimpleDataRow-commission-downloadPDFButton"
                      values={{ month: data.month }}
                      components={{
                        span: <span className="sr-only" />,
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </LoadingOverlay>
          <PDFDialog
            title={`${stringFormatingToPascal(
              data?.document?.typeOfDocument
            )} Preview`}
            description={desc}
            pdfSource={pdfSource}
            dialogState={pdfDialogState}
            setDialogState={setPdfDialogState}
          />
        </>
      );
      break;
    case "statement":
      dynamicMarkUp = (
        <>
          <LoadingOverlay
            active={loading}
            spinner={
              <EverlakeLoadingSpinner
                spinnerSize="xs"
                loadingTextMargin="2"
                loadingText={t("SimpleDataRow-statement-spinnerLoadingText")}
              />
            }
            styles={EverlakeLoadingOverlayStyle}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4 xl:gap-4">
              <div className="col-span-2">
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-statement-statementNameText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data.statementName}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-statement-dateText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data?.dateAdded}
                </div>
              </div>
              <div className="justify-self-end">
                <div>
                  {data?.document?.document?.name && (
                    <>
                      <div className="text-xs text-theme-text-tertiary">
                        {t("SimpleDataRow-statement-youCanText")}
                      </div>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        {/* <button
                          className="ui-btn-hyperlink font-semibold"
                          onClick={() => {
                            if (data?.document?.document?.name) {
                              setLoading(true);
                              dispatch(
                                getImage({
                                  imageFileName:
                                    data?.document?.document?.name ?? "",
                                  policyId: data?.document?.policy?.contractID,
                                })
                              )
                                .then((response) => {
                                  setLoading(false);
                                  var byteArray = base64ToArrayBuffer(
                                    response.payload.document.contentStream.type
                                  );
                                  let blob = new Blob([byteArray], {
                                    type: "application/octet-stream",
                                  });
                                  fileSaver.saveAs(
                                    blob,
                                    `${data?.document?.document?.description}.pdf`
                                  );
                                })
                                .catch((error) => {
                                  console.error("Error", error.message);
                                  setPdfDialogState(true);
                                  setLoading(false);
                                  setDesc(
                                    data?.document?.document?.description
                                  );
                                  setPdfSource("");
                                });
                            }
                          }}
                        >
                          View
                          <span className="sr-only">
                            {" "}
                            {data?.document?.description}
                          </span>
                        </button>
                        <span className="mx-2">/</span> */}
                        <button
                          className="ui-btn-hyperlink font-semibold"
                          onClick={() => {
                            if (data?.document?.document?.name) {
                              setLoading(true);
                              dispatch(
                                getImage({
                                  imageFileName:
                                    data?.document?.document?.name ?? "",
                                  policyId: data?.document?.policy?.contractID,
                                })
                              )
                                .then((response) => {
                                  setLoading(false);
                                  let byteArray = base64ToArrayBuffer(
                                    response.payload.document.contentStream.type
                                  );
                                  let blob = new Blob([byteArray], {
                                    type: t(
                                      "AgentSimpleDataRow-applicationpdf"
                                    ),
                                  });
                                  fileSaver.saveAs(
                                    blob,
                                    `${data?.document?.document?.description}.pdf`
                                  );
                                })
                                .catch((error) => {
                                  console.error("Error", error.message);
                                  setPdfDialogState(true);
                                  setLoading(false);
                                  setDesc(
                                    data?.document?.document?.description
                                  );
                                  setPdfSource("");
                                });
                            }
                          }}
                        >
                          {t("SimpleDataRow-statement-downloadText")}
                          <span className="sr-only">
                            {" "}
                            {data?.document?.description}
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </LoadingOverlay>
          <PDFDialog
            title={`${data?.document?.document?.name ?? ""} Preview`}
            description={desc}
            pdfSource={pdfSource}
            dialogState={pdfDialogState}
            setDialogState={setPdfDialogState}
          />
        </>
      );
      break;
    case "bookOfBusiness":
      dynamicMarkUp = (
        <>
          <LoadingOverlay
            active={loading}
            spinner={
              <EverlakeLoadingSpinner
                spinnerSize="xs"
                loadingTextMargin="2"
                loadingText={t(
                  "SimpleDataRow-bookOfBusiness-spinnerLoadingText"
                )}
              />
            }
            styles={EverlakeLoadingOverlayStyle}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6">
              <div className="">
                <div className="text-lg font-semibold text-theme-text-secondary">
                  {data.policyName}
                </div>
                <div className="text-xs text-theme-text-tertiary">
                  {getPolicyNumberTitle(data)} {data.oldPolicy}
                  {data ? ` : ${getPolicyType(data)}` : ""}
                </div>
              </div>
              <div className="sm:text-right">
                {data?.status && data?.status !== "" && (
                  <Badge
                    status={data.policyStatus}
                    statusText={getBadgeText(data)}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 md:grid-cols-4 xl:mt-6">
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-bookOfBusiness-dueDateText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {data?.dueDate}
                </div>
              </div>
              <div>
                {data?.status?.toLowerCase() === "lapsed" && (
                  <div className="text-xs text-theme-text-tertiary">
                    {t("SimpleDataRow-bookOfBusiness-policyLapsed")}
                  </div>
                )}
                {!isOldAlicUlPolicy(data) &&
                  isPolicyInGrace(data) &&
                  data.status?.toLowerCase() !== "extended term" && (
                    <div className="text-xs text-theme-text-tertiary">
                      <button
                        className={`${
                          lapsedOn
                            ? "text-xs text-theme-text-tertiary"
                            : "ui-btn-hyperlink font-semibold"
                        }`}
                        onClick={() => {
                          if (!lapsedOn) getLapseOn(data);
                        }}
                      >
                        {t("SimpleDataRow-bookOfBusiness-lapseDateText")}
                      </button>
                    </div>
                  )}
                <div className="text-sm font-semibold text-theme-text-secondary">
                  <span
                    className={`${
                      data?.status?.toLowerCase() === "lapsed"
                        ? "text-ui-red"
                        : ""
                    }`}
                  >
                    {lapsedOn}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("SimpleDataRow-bookOfBusiness-premiumAmountText")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {stringFormattingToCurrency(data?.premiumAmount)}
                </div>
              </div>
              <div className="">
                <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-1 sm:gap-2">
                  {(data?.policyStatus === "problem" ||
                    data?.policyStatus === "risk") &&
                    data?.status?.toLowerCase() !== "lapsed" && (
                      <div className="col-start-2 row-start-1 place-self-end sm:col-auto sm:row-auto">
                        <button
                          className="ui-btn-primary font-semibold"
                          onClick={() => {
                            setPolicyId(data.policyNumber);
                            setIsOpenSendReminder(true);
                          }}
                        >
                          {t("SimpleDataRow-bookOfBusiness-sendReminderText")}
                        </button>
                      </div>
                    )}
                  <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
                    <Link
                      className="text-xs text-left"
                      to={`/policies/${data.policyNumber}`}
                    >
                      {t("SimpleDataRow-bookOfBusiness-viewcontract-text")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </LoadingOverlay>
        </>
      );
      break;
    case "annuityBookOfBusiness":
      dynamicMarkUp = (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6">
            <div className="text-lg font-semibold text-theme-text-secondary">
              {data.policyName}
            </div>
            <div className="sm:text-right">
              {data?.status && data?.status !== "" && (
                <Badge
                  status={data.policyStatus}
                  statusText={getBadgeText(data)}
                />
              )}
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("SimpleDataRow-annuityBookOfBusiness-contractNumberText")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {data?.oldPolicy}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("SimpleDataRow-annuityBookOfBusiness-policyTypeText")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {t("SimpleDataRow-annuityBookOfBusiness-annuityText")}
              </div>
            </div>
          </div>
          <div className="">
            <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-1 sm:gap-2">
              <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
                <Link
                  className="text-xs text-left"
                  to={`/policies/${data.policyNumber}`}
                >
                  {t("SimpleDataRow-annuityBookOfBusiness-viewPolicyText")}
                </Link>
              </div>
            </div>
          </div>
        </>
      );
      break;
    case "policylookup":
      dynamicMarkUp = (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6">
            <div className="">
              <div className="text-lg font-semibold text-theme-text-secondary">
                {data.policyName}
              </div>
              <div className="text-xs text-theme-text-tertiary">
                {getPolicyNumberTitle(data)} {data.oldPolicy}
                {data ? ` : ${getPolicyType(data)}` : ""}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 md:grid-cols-4 xl:mt-6">
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("SimpleDataRow-policylookup-dueDateText")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {data?.premiumDueDate}
              </div>
            </div>
            <div></div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("SimpleDataRow-policylookup-premiumAmountText")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {stringFormattingToCurrency(data?.modalPremiumAmountTotal)}
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-1 sm:gap-2">
                <div className="col-start-2 row-start-1 place-self-end sm:col-auto sm:row-auto">
                  <button
                    className="ui-btn-primary font-semibold"
                    disabled={!isAllowedToMakeCCPayment(data, "onetimepayment")}
                    onClick={() => {
                      setOldPolicyId(data.oldPolicy);
                      setPolicyId(data.contractID);
                      setAmount(data.modalPremiumAmountTotal);
                    }}
                  >
                    {t("SimpleDataRow-policylookup-selectButton")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
      break;
    case "policyResult":
      dynamicMarkUp = (
        <>
          <div className="relative">
            {data.banner && (
              <div className={`ui-badge-bar ui-badge-bar-${data.banner}`}></div>
            )}
            <div className="relative ui-card-bar px-3 py-4 lg:p-4 lg:pt-6">
              <div className="grid grid-cols-2 items-start gap-4 mr-10 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:gap-6">
                <div className="col-span-2 sm:col-span-3 md:col-span-5 lg:col-span-2">
                  <div className="text-xxs text-theme-text-tertiary">
                    {getPolicyNumberTitle(data)} {data?.oldPolicy}
                  </div>
                  <div className="">
                    <span className="mr-2 md:hidden">
                      {data?.status && data?.status !== "" && (
                        <Badge
                          iconOnly="true"
                          status={data.badgeStatus}
                          statusText={getBadgeText(data)}
                          srPrefix="Policy"
                        />
                      )}
                    </span>
                    <span className="text-lg font-semibold text-theme-text-secondary">
                      {data.policyTitle}
                    </span>
                  </div>
                </div>
                <div className="hidden md:grid">
                  <div className="text-xs text-theme-text-tertiary">
                    {t("SimpleDataRow-policyResult-insuredNameText")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data.insuredName}
                  </div>
                </div>

                <div className="hidden md:grid">
                  <div className="text-xs text-theme-text-tertiary">
                    {t("SimpleDataRow-policyResult-productNameText")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data.productName}
                  </div>
                </div>
                {/* empty div for spacing */}
                <div>
                  {/* <div className="text-xs text-theme-text-tertiary">
                    {t("SimpleDataRow-policyResult-coverageText")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(data.coverage)}
                  </div> */}
                </div>
                <div className=""></div>
                <div className="col-span-2 text-right sm:col-span-1">
                  <div className="hidden md:block">
                    {data?.status && data?.status !== "" && (
                      <Badge
                        status={data.badgeStatus}
                        statusText={getBadgeText(data)}
                        srPrefix="Policy"
                      />
                    )}
                  </div>
                  {(data.badgeStatus === "problem" ||
                    data.badgeStatus === "risk") && (
                    <button
                      className="ui-btn-primary mt-2"
                      onClick={() => {
                        setPolicyId(data.policyNumber);
                        setIsOpenSendReminder(true);
                      }}
                    >
                      <Trans
                        i18nKey="SimpleDataRow-policyResult-sendReminderButton"
                        values={{ policyNumber: data.policyNumber }}
                        components={{
                          span: <span className="sr-only" />,
                        }}
                      />
                    </button>
                  )}
                </div>
              </div>
              <Link
                to={`/policies/${data.policyNumber}`}
                className="ui-btn-card-arrow rounded-none border-t-0 border-r-0 border-b-0"
                aria-label={t("SimpleDataRow-policyResult-linkAriaLabel", {
                  policyNumber: data.policyNumber,
                })}
              >
                <svg
                  className="ui-icon w-8 h-8"
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href="#ui-icon-next"></use>
                </svg>
              </Link>
            </div>
          </div>
        </>
      );
      break;
    case "completedTraining":
      dynamicMarkUp = (
        <div className="grid grid-cols-1 gap-4 place-content-between sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:gap-6">
          <div className="col-span-1 sm:col-span-2 md:col-span-1">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.name}
            </div>
            <div className="text-xs text-theme-text-tertiary">
              {data.category}
            </div>
          </div>

          <div className="">
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-completedTraining-completedDateText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.completedDate}
            </div>
          </div>

          <div className="">
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-completedTraining-goodForText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.goodForDuration}
            </div>
          </div>

          <div className="">
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-completedTraining-anotherFieldText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.another}
            </div>
          </div>

          <div>
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-completedTraining-youCanText")}
            </div>
            <div className="text-sm">
              <button className="ui-btn-hyperlink font-semibold">
                <Trans
                  i18nKey="SimpleDataRow-completedTraining-viewLink"
                  values={{ name: data.name }}
                  components={{
                    span: <span className="sr-only" />,
                    Link: <Link to={`#`} />,
                  }}
                />
              </button>
              <span className="mx-2">/</span>
              <button className="ui-btn-hyperlink font-semibold">
                <Trans
                  i18nKey="SimpleDataRow-completedTraining-downloadCertificateLink"
                  values={{ name: data.name }}
                  components={{
                    span: <span className="sr-only" />,
                    Link: <Link to={`#`} />,
                  }}
                />
              </button>
            </div>
          </div>
        </div>
      );
      break;
    case "customerServicesAndClaims":
      {
        const [statusClass, statusText] = odsClaimStatusConversion(
          data.claimStatus,
          data.claimType
        );
        dynamicMarkUp = (
          <div className="p-4 border-t border-theme-rule-light grow">
            <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch ml-5">
              <div className="flex flex-grow self-start text-xxs font-bold">
                {t("CustomerPolicyDetails-claim-service-request-id")}{" "}
                {data?.claimType?.toLowerCase() === "dis"
                  ? data.claimNumber
                  : data.deathClaimNumber}
              </div>
              {/* <div className="flex self-end text-xxs text-theme-text-tertiary text-right">
              Submitted: {data.submittedDate}
            </div> */}
            </div>
            <div className="flex justify-start items-stretch">
              <div className="flex pt-2 items-start flex-shrink-0 w-5">
                {data.showDot && <StatusDot status={statusClass} />}
              </div>
              <div className="flex flex-col flex-grow self-start">
                <div className="text-sm font-semibold text-theme-text-secondary mt-1">
                  {type === "service"
                    ? "Service request"
                    : `Claim on ${getName(data?.deceased)}`}
                </div>
                <div className="text-xs mt-1">
                  {t("CustomerPolicyDetails-claim-life-policy", {
                    values:
                      getTypeOfPolicy(data?.productType) === "annuity"
                        ? "Annuity Contract"
                        : "Life Contract",
                  })}
                  {data.oldPolicy}
                </div>
                <div className="text-xs mt-2">
                  <Pill status={statusClass} statusText={statusText} />
                </div>
              </div>
              <div className="self-center">
                <Link
                  className="ui-btn-arrow"
                  to={`/services-claims/${
                    type === "service" ? "services" : "claims"
                  }/${data?.claimNumber}`}
                >
                  <span className="sr-only">
                    <Trans
                      defaults={t("CustomerPolicyDetails-claim-view-link")}
                      values={type === "service" ? "service" : "claim"}
                    />
                  </span>
                  <svg
                    className="ui-icon w-8 h-8"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-next"></use>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        );
      }
      break;
    case "services":
      {
        dynamicMarkUp = (
          <div className="border-theme-rule-light mr-4 ml-4">
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-sm font-semibold text-theme-text-secondary">
                {convertToTitleCase(data?.desc)}
              </div>
              <div className="self-center">
                <Link
                  className="ui-btn-arrow"
                  to={`/services-claims/services?taskID=${data.taskID}`}
                >
                  <span className="sr-only">
                    {t("SimpleDataRow-services-viewServiceDetailsText")}
                  </span>
                  <svg
                    className="ui-icon w-8 h-8"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-next"></use>
                  </svg>
                </Link>
              </div>
            </div>
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-xs">
                {t("CustomerPolicies-policy-number")} {data.oldPolicy}
              </div>
            </div>
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-xs">
                {data?.status?.toLowerCase() === "c" ? (
                  <Pill
                    status={t("SimpleDataRow-good")}
                    statusText={t("SimpleDataRow-completed")}
                  />
                ) : (
                  <Pill
                    status={t("SimpleDataRow-risk")}
                    statusText={t("SimpleDataRow-pending")}
                  />
                )}
              </div>
              <div className=" text-xs">
                {t("SimpleDataRow-service-id")} {data.taskID}
              </div>
              <div className="text-xs pr-4">{getServiceRequestDate(data)}</div>
            </div>
            <hr />
          </div>
        );
      }
      break;
    case "agentServicesAndClaims":
      {
        const [statusClass, statusText] = odsClaimStatusConversion(
          data.claimStatus,
          data?.claimType?.toLowerCase() !== "dth" && data?.claimType !== "dis"
            ? "serviceRequest"
            : data.claimType
        );
        dynamicMarkUp = (
          <div className="border-theme-rule-light mr-4 ml-4">
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-sm font-semibold text-theme-text-secondary pt-2">
                {convertToTitleCase(data?.primaryOwnerFullName)}
              </div>
              <div className="self-center">
                <Link
                  className="ui-btn-arrow"
                  to={`${
                    data?.claimType?.toLowerCase() === "dth" ||
                    data?.claimType?.toLowerCase() === "dis"
                      ? "/claims"
                      : "/services"
                  }`}
                >
                  <span className="sr-only">
                    {t("SimpleDataRow-services-viewServiceDetailsText")}
                  </span>
                  <svg
                    className="ui-icon w-8 h-8"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-next"></use>
                  </svg>
                </Link>
              </div>
            </div>
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-xs">
                {t("AgentCard-Badge-sr-prefix")} {data.oldPolicy}
              </div>
            </div>
            <div className="flex sm:flex-row justify-between items-stretch">
              <div className="text-xs">
                <Pill status={statusClass} statusText={statusText} />
              </div>
              <div className="text-xs">
                <span className="pr-4">
                  {data?.claimType?.toLowerCase() === "dth" ? "#" : ""}
                  {data?.claimType?.toLowerCase() === "dth"
                    ? data.claimNumber
                    : convertToTitleCase(data?.desc)}
                </span>
                <span>{getServiceRequestDate(data)}</span>
              </div>
            </div>
            <hr />
          </div>
        );
      }
      break;
    case "form":
      dynamicMarkUp = (
        <LoadingOverlay
          active={loading}
          spinner={
            <EverlakeLoadingSpinner
              spinnerSize="xs"
              loadingTextMargin="2"
              loadingText={t("CustomerForms-simpleDataRow-forms-loading-text")}
            />
          }
          styles={EverlakeLoadingOverlayStyle}
        >
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <div className="text-sm font-semibold text-theme-text-secondary">
                {isFlagEnabled("ENABLE_FORMS_WIZARD")
                  ? data.formTitle
                  : data.nttLetterDescription}
              </div>
              {!hideFormSubTitle && (
                <div className="text-xs text-theme-text-tertiary">
                  {isFlagEnabled("ENABLE_FORMS_WIZARD")
                    ? data.formSubTitle
                    : data.formName}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <div className="flex text-sm items-center gap-3">
                {isCSRUser && data?.id ? (
                  <button
                    className="ui-btn-hyperlink font-semibold"
                    type="button"
                    onClick={() => onSendEmailClick?.(data)}
                  >
                    Send Email
                  </button>
                ) : null}
                {data?.templateId && (
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    <button
                      className="ui-btn-hyperlink font-semibold"
                      aria-label="download pdf"
                      type="button"
                      onClick={() => {
                        if (data?.templateId) {
                          setDesc(data?.nttLetterDescription);
                          getPdfFormDocument(data);
                        }
                      }}
                    >
                      {t("CustomerForms-simpleDataRow-forms-download-pdf")}
                      <span className="sr-only">
                        {" "}
                        {data?.document?.description}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PDFDialog
            title={`${data?.formName ?? ""} Preview`}
            description={desc}
            pdfSource={pdfSource}
            dialogState={pdfDialogState}
            setDialogState={setPdfDialogState}
          />
        </LoadingOverlay>
      );
      break;
    case "assistant":
      dynamicMarkUp = (
        <div className="grid grid-cols-1 gap-4 place-content-between sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:gap-6">
          <div className="col-span-1 sm:col-span-2 md:col-span-1">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.firstName} {data.lastName}
            </div>
          </div>

          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.isRegistered === 1 ? (
                t("SimpleDataRow-assistant-RegisteredText")
              ) : (
                <div style={{ color: "red" }}>
                  {t("SimpleDataRow-assistant-notRegisteredText")}
                </div>
              )}
            </div>
            <div className="text-xs text-theme-text-tertiary">
              {data.isRegistered === 1 ? (
                "Yes"
              ) : (
                <button
                  className="ui-btn-hyperlink font-semibold"
                  onClick={() => setopenReminderAgentAssistantDialog(true)}
                >
                  {" "}
                  <Link className="ui-link-icon font-bold text-xs" to={``}>
                    {t("SimpleDataRow-assistant-sendReminderButton")}
                  </Link>{" "}
                </button>
              )}
              <AgentAssistantReminderModel
                openAssistantModel={openReminderAgentAssistantDialog}
                setopenAssistantModel={setopenReminderAgentAssistantDialog}
                assistantfname={data.firstName}
                assistantlname={data.lastName}
                assistantemailId={data.emailId}
              />
            </div>
          </div>
          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistant-addedText")}
            </div>
            <div className="text-xs text-theme-text-tertiary">
              {data.createdOn
                ? new Date(data.createdOn).toLocaleDateString()
                : ""}
            </div>
          </div>

          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistant-informationPermissionsText")}
            </div>
            <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
              <button
                className="ui-btn-hyperlink font-semibold"
                onClick={() => setopenAgentAssistantDialog(true)}
              >
                <Link className="ui-link-icon font-bold text-xs" to={``}>
                  {t("SimpleDataRow-assistant-editText")}
                </Link>
              </button>
              <AssistantInfoDialog
                dialogState={openAgentAssistantDialog}
                setDialogState={setopenAgentAssistantDialog}
                title={t("SimpleDataRow-assistantInfoDialog-title")}
                description={t("SimpleDataRow-assistantInfoDialog-description")}
                assistantfname={data.firstName}
                assistantlname={data.lastName}
                assistantmobile={data.mobile}
                assistantemail={data.emailId}
                assistantzip={data.postalCode}
                assistantviewcommission={data.viewCommissions}
                assistantuserid={data.userId}
                operation="update"
                getuserPermissions={getuserPermissions}
                setPageLoad={setPageLoad}
              />
            </div>
          </div>
          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistant-editAccessText")}
            </div>
            <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
              <button
                className="ui-btn-hyperlink font-semibold"
                onClick={() => setopenTerminateAgentAssistantDialog(true)}
              >
                <Link className="ui-link-icon font-bold text-xs" to={``}>
                  {t("SimpleDataRow-assistant-terminateText")}
                </Link>
              </button>
              <AssistantTerminateDialog
                dialogState={openTerminateAgentAssistantDialog}
                setDialogState={setopenTerminateAgentAssistantDialog}
                datafirstname={data.firstName}
                datalastname={data.lastName}
                datauserid={data.userId}
                getuserPermissions={getuserPermissions}
                setPageLoad={setPageLoad}
              />
            </div>
          </div>
        </div>
      );
      break;
    case "assistantterminated":
      dynamicMarkUp = (
        <div className="grid grid-cols-1 gap-4 place-content-between sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:gap-6">
          <div className="col-span-1 sm:col-span-2 md:col-span-1">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.firstName} {data.lastName}
            </div>
          </div>

          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistantterminated-registeredText")}
            </div>
            <div className="text-xs text-theme-text-tertiary">No</div>
          </div>
          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistantterminated-terminatedText")}
            </div>
            <div className="text-xs text-theme-text-tertiary">
              {data.createdOn
                ? new Date(data.createdOn).toLocaleDateString()
                : ""}
            </div>
          </div>

          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistantterminated-contactInfoText")}
            </div>
            <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
              <button
                className="ui-btn-hyperlink font-semibold"
                onClick={() => setopenAgentAssistantDialog(true)}
              >
                <Link className="ui-link-icon font-bold text-xs" to={``}>
                  {t("SimpleDataRow-assistantterminated-viewText")}
                </Link>
              </button>

              <AssistantInfoDialog
                dialogState={openAgentAssistantDialog}
                setDialogState={setopenAgentAssistantDialog}
                title={t(
                  "SimpleDataRow-openAgentAssistant-assistantInfoDialog-title"
                )}
                description={t(
                  "SimpleDataRow-openAgentAssistant-assistantInfoDialog-description"
                )}
                assistantfname={data.firstName}
                assistantlname={data.lastName}
                assistantmobile={data.mobile}
                assistantemail={data.emailId}
                assistantzip={data.postalCode}
                assistantviewcommission={data.viewCommissions}
                assistantisactive={data.isActive}
                operation="view"
              />
            </div>
          </div>
          <div className="">
            <div className="text-sm font-semibold text-theme-text-secondary">
              {t("SimpleDataRow-assistantterminated-editAccessText")}
            </div>
            <div className="col-start-1 row-start-1 place-self-start sm:col-auto sm:row-auto sm:place-self-end">
              <button
                className="ui-btn-hyperlink font-semibold"
                onClick={() => setopenAgentReactivateDialog(true)}
              >
                <Link className="ui-link-icon font-bold text-xs" to={``}>
                  {t("SimpleDataRow-assistantterminated-reactivateLink")}
                </Link>
              </button>
              <AssistantInfoDialog
                dialogState={openAgentReactivateDialog}
                setDialogState={setopenAgentReactivateDialog}
                title={t(
                  "SimpleDataRow-openAgentReactivate-assistantInfoDialog-title"
                )}
                description={t(
                  "SimpleDataRow-openAgentReactivate-assistantInfoDialog-description"
                )}
                assistantfname={data.firstName}
                assistantlname={data.lastName}
                assistantmobile={data.mobile}
                assistantemail={data.emailId}
                assistantzip={data.postalCode}
                assistantviewcommission={data.viewCommissions}
                assistantuserid={data.userId}
                operation="reactivate"
                getuserPermissions={getuserPermissions}
                setPageLoad={setPageLoad}
              />
            </div>
          </div>
        </div>
      );
      break;
    case "withdrawalHistory":
      dynamicMarkUp = (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 xl:gap-4">
          <div className="md:col-span-2">
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-withdrawalHistory-contractNumberText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {data.oldPolicy}
            </div>
          </div>
          <div>
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-withdrawalHistory-withdrawalAmountText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {stringFormattingToCurrency(data.amount)}
            </div>
          </div>
          <div>
            <div className="text-xs text-theme-text-tertiary">
              {t("SimpleDataRow-withdrawalHistory-withdrawalDateText")}
            </div>
            <div className="text-sm font-semibold text-theme-text-secondary">
              {moment(data.date).format("MM/DD/YYYY")}
            </div>
          </div>
        </div>
      );
      break;
    default:
      break;
  }

  switch (type) {
    case "policyResult":
    case "customerServicesAndClaims":
      return <div className="relative mt-4">{dynamicMarkUp}</div>;
    case "services":
      return <div className="relative mt-4">{dynamicMarkUp}</div>;
    case "agentServicesAndClaims":
      return <div className="relative mt-4">{dynamicMarkUp}</div>;
    default:
      return (
        <div className="ui-card-bar relative mt-4">
          <div className="px-3 py-2 lg:p-4">{dynamicMarkUp}</div>
        </div>
      );
  }
}
